import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Stack,
  Avatar,
  Typography,
  TableHead,
  TableSortLabel,
} from "@mui/material";
import React, { useState } from "react";
import { Box } from "@mui/system";
import "../WebsiteTarget.css";
import { formatDate } from "date-fns";
import { formatPercentage } from "../../../../../utils/helpers";

interface Props {
  MicroTableHeadings: { [key: string]: string };
  selectedTargetSharedUid: any;
  websiteMicroTarget: any;
  selectedDataSet: any;
  formatDate: (date: string) => string;
}

const formatNumber = (
  value: number | string,
  type: "currency" | "percentage" | "number"
) => {
  const num = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(num)) return value;

  switch (type) {
    case "currency":
      return `${num.toLocaleString("en-US", { maximumFractionDigits: 0 })}$`;
    case "percentage":
      return `${num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;
    default:
      return num.toLocaleString("en-US");
  }
};

const MicroTargets = ({
  MicroTableHeadings,
  selectedTargetSharedUid,
  selectedDataSet,
  websiteMicroTarget,
}: Props) => {
  const [page] = useState(0);
  const [rowsPerPage] = useState(10);
  const headCells = Object.entries(MicroTableHeadings);
  const filteredRows = websiteMicroTarget.filter(
    (row: any) => row?.website_target_shared_uid === selectedTargetSharedUid
  );

  const columns: {
    [key: string]: {
      id: number;
      KEY: string;
      label: string;
      sortable: boolean;
    }[];
  } = {
    "Key Metrics": [
      { id: 0, KEY: "geo", label: "Geo", sortable: true },
      { id: 1, KEY: "spend", label: "Spend", sortable: true },
      { id: 2, KEY: "clicks", label: "Clicks", sortable: true },
      { id: 3, KEY: "ftd", label: "FTD", sortable: true },
      { id: 4, KEY: "cvr", label: "CVR", sortable: true },
      { id: 5, KEY: "cac", label: "CAC", sortable: true },
      { id: 6, KEY: "profit", label: "Profit", sortable: true },
      { id: 7, KEY: "nrpu", label: "NRPU", sortable: true },
      { id: 8, KEY: "modified", label: "Last Modified", sortable: true },
      { id: 9, KEY: "modified_by", label: "Modified By", sortable: true },
    ],
    Income: [
      { id: 0, KEY: "geo", label: "Geo", sortable: true },
      { id: 1, KEY: "cpa", label: "CPA", sortable: true },
      { id: 2, KEY: "rs", label: "RS", sortable: true },
      { id: 3, KEY: "total_income", label: "Total Income", sortable: true },
      { id: 4, KEY: "cpa_pv", label: "CPA PV", sortable: true },
      { id: 5, KEY: "rs_pv", label: "RS PV", sortable: true },
      { id: 6, KEY: "pv", label: "PV", sortable: true },
      { id: 7, KEY: "net_revenue", label: "Net Revenue", sortable: true },
      { id: 8, KEY: "avg_rs_deal_p", label: "Avg RS Deal", sortable: true },
      { id: 9, KEY: "epc", label: "EPC", sortable: true },
      { id: 10, KEY: "epc_cpa", label: "EPC CPA", sortable: true },
      { id: 11, KEY: "epc_rs", label: "EPC RS", sortable: true },
    ],
    Performance: [
      { id: 0, KEY: "geo", label: "Geo", sortable: true },
      { id: 1, KEY: "growth_target", label: "Growth Target", sortable: true },
      { id: 2, KEY: "active_users", label: "Active Users", sortable: true },
      {
        id: 3,
        KEY: "old_active_users",
        label: "Old Active Users",
        sortable: true,
      },
      {
        id: 4,
        KEY: "acquired_active_users",
        label: "Acquired Active Users",
        sortable: true,
      },
      { id: 5, KEY: "churn_rate", label: "Churn Rate", sortable: true },
      {
        id: 6,
        KEY: "ratio_between_acquired_to_ftd",
        label: "Acquired To FTD",
        sortable: true,
      },
      {
        id: 7,
        KEY: "avg_net_rev_per_active_user",
        label: "Avg Net Rev To Active Users",
        sortable: true,
      },
      {
        id: 8,
        KEY: "br_to_ng_active_rs_ratio",
        label: "BR to Ng Active RS",
        sortable: true,
      },
    ],
    All: [
      { id: 0, KEY: "geo", label: "Geo", sortable: true },
      { id: 1, KEY: "spend", label: "Spend", sortable: true },
      { id: 2, KEY: "clicks", label: "Clicks", sortable: true },
      { id: 3, KEY: "ftd", label: "FTD", sortable: true },
      { id: 4, KEY: "cvr", label: "CVR", sortable: true },
      { id: 5, KEY: "cac", label: "CAC", sortable: true },
      { id: 6, KEY: "profit", label: "Profit", sortable: true },
      { id: 7, KEY: "nrpu", label: "NRPU", sortable: true },
      { id: 10, KEY: "cpa", label: "CPA", sortable: true },
      { id: 11, KEY: "rs", label: "RS", sortable: true },
      { id: 12, KEY: "total_income", label: "Total Income", sortable: true },
      { id: 13, KEY: "cpa_pv", label: "CPA PV", sortable: true },
      { id: 14, KEY: "rs_pv", label: "RS PV", sortable: true },
      { id: 15, KEY: "pv", label: "PV", sortable: true },
      { id: 16, KEY: "net_revenue", label: "Net Revenue", sortable: true },
      { id: 17, KEY: "avg_rs_deal_p", label: "Avg RS Deal", sortable: true },
      { id: 18, KEY: "epc", label: "EPC", sortable: true },
      { id: 19, KEY: "epc_cpa", label: "EPC CPA", sortable: true },
      { id: 20, KEY: "epc_rs", label: "EPC RS", sortable: true },
      { id: 21, KEY: "growth_target", label: "Growth Target", sortable: true },
      { id: 22, KEY: "active_users", label: "Active Users", sortable: true },
      {
        id: 23,
        KEY: "old_active_users",
        label: "Old Active Users",
        sortable: true,
      },
      {
        id: 24,
        KEY: "acquired_active_users",
        label: "Acquired Active Users",
        sortable: true,
      },
      { id: 25, KEY: "churn_rate", label: "Churn Rate", sortable: true },
      {
        id: 26,
        KEY: "ratio_between_acquired_to_ftd",
        label: "Acquired To FTD",
        sortable: true,
      },
      {
        id: 27,
        KEY: "avg_net_rev_per_active_user",
        label: "Avg Net Rev To Active Users",
        sortable: true,
      },
      {
        id: 28,
        KEY: "br_to_ng_active_rs_ratio",
        label: "BR to Ng Active RS",
        sortable: true,
      },
      { id: 29, KEY: "modified", label: "Last Modified", sortable: true },
      { id: 30, KEY: "modified_by", label: "Modified By", sortable: true },
    ],
  };

  const selectedColumns = columns[selectedDataSet] || [];

  return (
    <>
      <Box sx={{ padding: "10px" }}>
        <Typography fontSize={16} sx={{ color: "#000000DE" }}>
          Micro - targets
        </Typography>
      </Box>

      <TableContainer
        className="microTargetListewrap"
        component={Paper}
        sx={{
          padding: "0px 0px",
          boxShadow: "none",
          borderStartStartRadius: "0px",
        }}
      >
        <Table aria-label="collapsible table" size="small">
          {selectedDataSet === "Performance" ? (
            <TableBody>
              <TableRow
                sx={{
                  "& > *": {
                    borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
                  },
                }}
              >
                <TableCell colSpan={headCells.length} align="center">
                  There is no data
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <>
              <TableHead>
                <TableRow>
                  {selectedColumns.map((header) => {
                    // Check if all values in this column are "N/A"
                    const isAllNA = filteredRows.every((row: any) => {
                      const value = row[header.KEY];
                      return value === undefined || value === "N/A";
                    });

                    // Only render header if not all values are "N/A"
                    return !isAllNA ? (
                      <TableCell key={header.id}>
                        <TableSortLabel disabled={!header.sortable}>
                          {header.label}
                        </TableSortLabel>
                      </TableCell>
                    ) : null;
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length > 0 ? (
                  filteredRows
                    ?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                    ?.map((row: any, rowIndex: any) => (
                      <React.Fragment key={rowIndex}>
                        <TableRow
                          sx={{
                            "& > *": {
                              borderBottom:
                                "1px solid rgba(224, 224, 224, 1) !important",
                            },
                          }}
                        >
                          {selectedColumns.map((column) => {
                            // Check if all values in this column are "N/A"
                            const isAllNA = filteredRows.every((row: any) => {
                              const value = row[column.KEY];
                              return value === undefined || value === "N/A";
                            });

                            // Skip rendering if all values are "N/A"
                            if (isAllNA) return null;

                            if (column.KEY === "modified_by") {
                              const value = row?.modified_by;
                              if (!value || value === "N/A") return null;
                              return (
                                <TableCell key={column.KEY} align="left">
                                  <Stack
                                    flexDirection={"row"}
                                    alignItems={"center"}
                                    gap={1}
                                  >
                                    <Avatar
                                      alt={value}
                                      src="#"
                                      sx={{ width: 24, height: 24 }}
                                    />
                                    {value}
                                  </Stack>
                                </TableCell>
                              );
                            } else if (column.KEY === "modified") {
                              const value = row?.modified;
                              if (!value || value === "N/A") return null;
                              return (
                                <TableCell key={column.KEY} align="left">
                                  {formatDate(value, "MMM d, yyyy, h:mm a")}
                                </TableCell>
                              );
                            } else if (
                              column.KEY === "growth_target" ||
                              column.KEY === "ratio_between_acquired_to_ftd" ||
                              column.KEY === "br_to_ng_active_rs_ratio"
                            ) {
                              const value = row[column.KEY];
                              if (value === undefined || value === "N/A")
                                return null;
                              return (
                                <TableCell key={column.KEY} align="left">
                                  {`${value}%`}
                                </TableCell>
                              );
                            } else if (
                              column.KEY === "avg_net_rev_per_active_user"
                            ) {
                              const value = row[column.KEY];
                              if (value === undefined || value === "N/A")
                                return null;
                              return (
                                <TableCell key={column.KEY} align="left">
                                  {`$${value}`}
                                </TableCell>
                              );
                            } else if ( column.KEY === "cvr") {
                                return (
                                  <TableCell key={column.KEY} align="left">
                                  {row[column.KEY] !== undefined || row[column.KEY] !== null ? formatPercentage(row[column.KEY]) : 0}
                                  </TableCell>
                                );
                              
                              } else if (
                              column.KEY === "avg_rs_deal_p"
                            ) {
                              const value = row[column.KEY];
                              if (value === undefined || value === "N/A")
                                return null;
                              return (
                                <TableCell key={column.KEY} align="left">
                                  {formatNumber(value, "percentage")}
                                </TableCell>
                              );
                            } else if (
                              [
                                "spend",
                                "profit",
                                "nrpu",
                                "cac",
                                "cpa",
                                "rs",
                                "total_income",
                                "cpa_pv",
                                "rs_pv",
                                "pv",
                                "net_revenue",
                                "epc",
                                "epc_cpa",
                                "epc_rs",
                              ].includes(column.KEY.toLowerCase())
                            ) {
                              const value = row[column.KEY];
                              if (value === undefined || value === "N/A")
                                return null;
                              return (
                                <TableCell key={column.KEY} align="left">
                                  {formatNumber(value, "currency")}
                                </TableCell>
                              );
                            } else if (column.KEY !== "month") {
                              const value = row[column.KEY];
                              if (value === undefined || value === "N/A")
                                return null;
                              return (
                                <TableCell key={column.KEY} align="left">
                                  {typeof value === "number"
                                    ? formatNumber(value, "number")
                                    : value}
                                </TableCell>
                              );
                            }
                            return null;
                          })}
                        </TableRow>
                      </React.Fragment>
                    ))
                ) : (
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    <TableCell colSpan={headCells.length} align="center">
                      There is no data
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </>
          )}
        </Table>
      </TableContainer>
    </>
  );
};

export default MicroTargets;
